import { useNavigate } from 'react-router-dom';
import { Film, Popcorn, Sparkles, ListFilter, Clock, Star, Tv, LogOut } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthProvider';
import { useSearchLimits } from '../../hooks/useSearchLimits';
import { SearchesLeftBadge } from '../ui/SearchesLeftBadge';
import { OutOfSearchesWall } from '../ui/OutOfSearchesWall';
import { Logo } from '../ui/Logo';
import { Footer } from '../Footer';

const features = [
  {
    icon: <Sparkles className="h-5 w-5" />,
    title: 'AI-Powered Recommendations',
    description: 'Get personalized movie suggestions based on your preferences and viewing history.'
  },
  {
    icon: <ListFilter className="h-5 w-5" />,
    title: 'Smart Filtering',
    description: 'Find movies that match your taste and preferences.'
  },
  {
    icon: <Clock className="h-5 w-5" />,
    title: 'Time-Based Selection',
    description: 'Choose movies from specific eras or discover timeless classics.'
  },
  {
    icon: <Star className="h-5 w-5" />,
    title: 'Preference Learning',
    description: 'The more you use MovieMe, the better it understands your taste.'
  }
];

const TMDB = 'https://image.tmdb.org/t/p/w500';
const TMDB_SM = 'https://image.tmdb.org/t/p/w342';

/** Collection wall — overlapping, dimmed posters behind the library band. */
const collectionWall = [
  { path: '/iiZZdoQBEYBv6id8su7ImL0oCbD.jpg', r: -4, y: 0 },   // Spider-Verse
  { path: '/d5iIlFn5s0ImszYzBPb8JPIfbXD.jpg', r: 3, y: 14 },   // Pulp Fiction
  { path: '/fNOH9f1aA7XRTzl1sAOx9iF553Q.jpg', r: -2, y: 4 },   // Back to the Future
  { path: '/oYuLEt3zVCKq57qu2F8dT7NIa6f.jpg', r: 5, y: 18 },   // Inception
  { path: '/wuMc08IPKEatf9rnMNXvIDxqP4W.jpg', r: -5, y: 2 },   // Harry Potter
  { path: '/3Ya1RurP2NqaYoiok4DmfO41rCC.jpg', r: 2, y: 10 },   // Friday
  { path: '/ty8TGRuvJLPUmAR1H1nRIsgwvim.jpg', r: -3, y: 20 },  // Gladiator
  { path: '/6oom5QYQ2yQTMJIbnvbkBL9cHo6.jpg', r: 4, y: 6 },    // The Lord of the Rings
  { path: '/RYMX2wcKCBAr24UyPD7xwmjaTn.jpg', r: -2, y: 16 },   // The Avengers
  { path: '/aKuFiU82s5ISJpGZp7YkIr3kCUd.jpg', r: 3, y: 2 },    // Goodfellas
];

const heroPosters = [
  { src: `${TMDB}/qJ2tW6WMUDux911r6m7haRef0WH.jpg`, alt: 'The Dark Knight poster', tilt: '-rotate-6', y: 'translate-y-6' },
  { src: `${TMDB}/9xjZS2rlVxm8SFx8kPC3aIGCOYQ.jpg`, alt: 'Titanic poster', tilt: '-rotate-3', y: 'translate-y-2' },
  { src: `${TMDB}/gEU2QniE6E77NI6lCU6MxlNBvIx.jpg`, alt: 'Interstellar poster', tilt: 'rotate-0', y: 'translate-y-0' },
  { src: `${TMDB}/hA2ple9q4qnwxp3hKVNhroipsir.jpg`, alt: 'Mad Max: Fury Road poster', tilt: 'rotate-3', y: 'translate-y-2' },
  { src: `${TMDB}/uluhlXubGu1VxU63X9VHCLWDAYP.jpg`, alt: 'The Hangover poster', tilt: 'rotate-6', y: 'translate-y-6' },
];


export function HomePage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { usage, loading: loadingUsage } = useSearchLimits();
  const outOfSearches = !loadingUsage && usage !== null && usage.remainingSearches <= 0;

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="grain relative min-h-screen">
      <header className="app-header px-4 pb-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3 sm:gap-5">
            <SearchesLeftBadge />
            <span className="hidden text-sm font-medium text-foreground sm:block">
              Welcome, {user?.user_metadata?.first_name || user?.email?.split('@')[0] || 'User'}
            </span>
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="inline-flex min-h-[44px] items-center text-sm font-medium text-foreground/90 transition-colors hover:text-white"
            >
              Account
            </button>
            <button
              onClick={handleSignOut}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-2 py-2 text-sm font-medium text-foreground/90 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-4 pb-20 pt-12 md:pt-16">
        {/* Hero */}
        <div className="mb-24 text-center">
          <h1 className="display animate-rise mx-auto mb-6 max-w-3xl text-4xl font-semibold leading-[1.08] sm:text-5xl md:text-[4.25rem]">
            Stop browsing.
            <br />
            <span className="text-gradient-dusk">Start watching.</span>
          </h1>
          <p className="animate-rise delay-150 mx-auto mb-9 max-w-xl text-lg text-muted-foreground">
            Tell us what you love, and MovieMe finds tonight's movie in minutes — not another hour of scrolling.
          </p>
          {outOfSearches ? (
            <OutOfSearchesWall />
          ) : (
            <button
              onClick={() => navigate('/conversation')}
              className="btn-marquee animate-rise delay-225 px-9 py-4 text-lg"
            >
              <Sparkles className="h-5 w-5" />
              Get Movie Recommendations
            </button>
          )}

          {/* Marquee poster fan */}
          <div className="animate-rise delay-300 pointer-events-none relative mx-auto mt-16 hidden max-w-3xl items-end justify-center gap-4 sm:flex">
            <div
              className="absolute inset-x-0 -top-10 h-64"
              style={{ background: 'radial-gradient(60% 90% at 50% 0%, hsl(352 65% 34% / 0.26), transparent 70%)' }}
            />
            {heroPosters.map((p, i) => (
              <img
                key={i}
                src={p.src}
                alt={p.alt}
                className={cn(
                  'poster-frame w-32 shadow-poster brightness-[0.72] saturate-[0.8] transition-transform duration-300 md:w-36',
                  p.tilt,
                  p.y
                )}
              />
            ))}
            <div
              className="absolute inset-x-0 -bottom-10 h-40"
              style={{ background: 'linear-gradient(to top, hsl(var(--background)) 20%, hsl(var(--background) / 0.6) 55%, transparent)' }}
            />
          </div>
        </div>

        {/* Features */}
        <div className="mb-20 grid gap-4 sm:grid-cols-2">
          {features.map((feature, index) => (
            <div
              key={index}
              className="cine-card group rounded-2xl p-6"
            >
              <div
                className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
                style={{ background: 'radial-gradient(circle, hsl(358 80% 45% / 0.18), transparent 70%)' }}
              />
              <div className="relative flex items-start gap-4">
                <div
                  className="rounded-xl p-3 text-primary-foreground shadow-glow-gold-soft"
                  style={{ background: 'linear-gradient(180deg, hsl(44 97% 62%), hsl(35 92% 49%))' }}
                >
                  {feature.icon}
                </div>
                <div>
                  <h3 className="mb-1.5 font-semibold text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Collection band — wall of posters */}
        <div className="cine-card group mb-8 overflow-hidden rounded-3xl">
          {/* Poster wall (desktop) */}
          <div
            className="pointer-events-none absolute inset-y-0 right-0 hidden w-[58%] overflow-hidden md:block"
            aria-hidden="true"
            style={{
              WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.35) 20%, rgba(0,0,0,0.85) 45%, black 65%)',
              maskImage: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.35) 20%, rgba(0,0,0,0.85) 45%, black 65%)',
            }}
          >
            <div
              className="h-full w-full"
              style={{
                WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, black 30%, black 72%, rgba(0,0,0,0.4) 100%)',
                maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, black 30%, black 72%, rgba(0,0,0,0.4) 100%)',
              }}
            >
            <div className="absolute -right-5 -top-10 rotate-[-3deg]">
              <div className="flex justify-end -space-x-5">
                {collectionWall.slice(0, 5).map((p, i) => (
                  <img
                    key={i}
                    src={`${TMDB_SM}${p.path}`}
                    alt=""
                    className="poster-frame w-36 brightness-[0.55] saturate-[0.75]"
                    style={{ transform: `rotate(${p.r}deg) translateY(${p.y}px)`, zIndex: i % 2 === 0 ? 2 : 1 }}
                  />
                ))}
              </div>
              <div className="-mt-3 flex translate-x-9 justify-end -space-x-5">
                {collectionWall.slice(5).map((p, i) => (
                  <img
                    key={i}
                    src={`${TMDB_SM}${p.path}`}
                    alt=""
                    className="poster-frame w-36 brightness-[0.55] saturate-[0.75]"
                    style={{ transform: `rotate(${p.r}deg) translateY(${p.y}px)`, zIndex: i % 2 === 0 ? 2 : 1 }}
                  />
                ))}
              </div>
            </div>
            </div>
          </div>

          <div className="relative z-10 space-y-4 p-8 md:min-h-[380px] md:max-w-[52%] md:p-12">
            <h2 className="display text-3xl font-semibold md:text-4xl">
              Start building your movie collection
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              Keep track of movies you want to watch and ones you've already seen. Rate your favorites and get better recommendations over time.
            </p>
            <div className="flex flex-wrap gap-4 pt-3">
              <button onClick={() => navigate('/watchlist')} className="btn-ghost gap-3 px-7 py-3.5 text-base">
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-full text-primary-foreground shadow-glow-gold-soft"
                  style={{ background: 'linear-gradient(180deg, hsl(44 97% 62%), hsl(35 92% 49%))' }}
                >
                  <Popcorn className="h-5 w-5" />
                </span>
                View Watchlist
              </button>
              <button onClick={() => navigate('/watched')} className="btn-ghost gap-3 px-7 py-3.5 text-base">
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-full text-white"
                  style={{ background: 'linear-gradient(180deg, hsl(358 78% 54%), hsl(352 80% 40%))', boxShadow: '0 0 20px hsl(358 80% 50% / 0.25)' }}
                >
                  <Film className="h-5 w-5" />
                </span>
                View Watched Movies
              </button>
            </div>
          </div>
        </div>

        {/* Coming Soon */}
        <div className="cine-card group rounded-3xl p-8">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-100"
            style={{ background: 'radial-gradient(circle, hsl(358 80% 45% / 0.22), transparent 70%)' }}
          />
          <div className="relative flex items-start gap-4">
            <div
              className="rounded-xl p-3 text-white"
              style={{ background: 'linear-gradient(180deg, hsl(358 78% 54%), hsl(352 80% 40%))', boxShadow: '0 0 20px hsl(358 80% 50% / 0.25)' }}
            >
              <Tv className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="mb-1.5 font-semibold text-foreground">
                New Features Coming Soon
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Seamless integration with your favorite streaming services! Soon, MovieMe will recommend movies available on the platforms you already use—making finding the perfect movie even easier!
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
